/**
 * Timeline ビルダー: backend が返す `TimelineItem` を、正本 doc
 * (`docs/implementation/20260821-timeline-events-remodeling.md`) が参照する
 * v1.0 (ClickUp Doc 137a79-48678) の Display Rules / UI Examples に沿った
 * `DisplayEvent` 配列へ展開し、 occurred_at 降順でマージする。
 *
 * - 対話 (entry): 1 エントリ = 1 イベント。 type ごとに 📞/💻/🏢/✉️。
 * - 接点ログ (contact_log): behavior_logs 由来の読み取り専用イベント。
 * - 商談/契約イベント (E-1/E-2/E-M/E-3/E-4・ClickUp 86eypmah0): すべて
 *   ⚙️ 営業イベント として 1 カテゴリに集約 (timeline.md §2/§3)。常時表示・
 *   条件付き表示・詳細表示のフィールド構成は v1.0 UI Unit (U5・CU-86eypmahh)
 *   の領分。旧 `opportunityToEvents` / `contractToEvents` (旧 opportunities /
 *   `contracts.signed_on` 単発イベント) は v1.0 (旧 opportunities の表示・件数・
 *   limit からの除外) で退役した。
 * - ai_recommendation はタイムライン非表示で backend が合成しないため
 *   `TimelineItem` にそもそも現れない (timeline.md §8 / Finding #2)。
 *
 * 裁定 C (弁護士タイムラインの表示集合 = イベント時点対象弁護士 ∪ individual
 * Party owner 本人) はサーバ側の投影クエリで既に解決済み
 * (`find_conclusion_timeline_events_for_lawyer` 等・`party_kind = 'individual'`
 * 判定を参照)。ここでは複製せず、返ってきた `target_lawyers` をそのまま表示に使う。
 */
import {
  SALES_EVENT_ICON,
  DIALOGUE_LOG_TYPE_ICONS,
  DIALOGUE_LOG_TYPE_LABELS,
} from '@shared/constants/choices';
import {
  CONTRACT_PIPELINE_LABELS,
  CONTRACT_STATUS_LABELS,
  type ContractPipeline,
  type ContractStatus,
} from '@shared/types/contract';
import {
  LOST_REASON_LABELS,
  SALES_STATUS_LABELS,
  type LostReason,
  type SalesStatus,
} from '@shared/types/sales-action';
import type { DialogueLog } from '@shared/types/dialogue-log';
import type {
  ContactLog,
  ContactLogSource,
  ContactLogType,
  ContractEvent,
  DealEvent,
  DealEventKind,
  ContractEventKind,
  TimelineItem,
  TimelineLawyerRef,
} from './model';

/** タイムラインのフィルタ単位。 issue #527: 全て / 接点ログ / 対話ログ / 商談・契約。 */
export type TimelineCategory = 'contact_log' | 'dialog' | 'sales_event';

/** カードのラベル付き 1 項目 (常時表示・条件付き表示・詳細表示のいずれか)。 */
export interface DisplayField {
  label: string;
  value: string;
}

export interface DisplayEvent {
  id: string;
  category: TimelineCategory;
  /** 接点ログ種別。 category === 'contact_log' のときだけ設定する。 */
  contactLogType?: ContactLogType;
  icon: string;
  typeLabel: string;
  occurred_at: string;
  title: string;
  summary: string | null;
  badges?: string[];
  /** 議事録本文の有無 (📄 アイコン表示判定)。 */
  hasMinutes?: boolean;
  /** 顧客側参加者 (弁護士 id)。 名前解決は表示側で行う。 */
  participantLawyerIds?: string[];
  /** 関連商談 id (関連情報行のバッジ)。 */
  relatedOpportunityId?: string | null;
  /**
   * 接点ログの対象弁護士 id (category === 'contact_log' のときだけ設定)。
   * firm 直の接点ログは null、 所属弁護士経由のログは弁護士 id が入る
   * (86eya0dva PR5: 法律事務所詳細タイムラインで「誰のログか」 を識別する)。
   * 名前解決は表示側 (lawyerNameById) で行う — DTO に lawyer_name は足さない。
   */
  lawyerId?: string | null;
  /**
   * 商談/契約イベント (category === 'sales_event') の常時表示フィールド
   * (v1.0 Display Rules「常時表示」)。
   */
  primaryFields?: DisplayField[];
  /** 該当する場合のみ追加する条件付き表示フィールド (v1.0「条件付き表示」)。 */
  conditionalFields?: DisplayField[];
  /** カードを過密にしないため詳細展開へ回すフィールド (v1.0「詳細表示」)。 */
  detailFields?: DisplayField[];
  /**
   * イベント時点の対象弁護士 (商談/契約とも)。法律事務所画面では常時表示、
   * 弁護士画面では表示中本人を除いた共同対象者を詳細表示へ回す
   * (v1.0 Visibility Rules — 表示先の判定 (裁定 C) 自体はサーバ導出済み。
   * これは表示内容の組み立てのみ)。
   */
  targetLawyers?: TimelineLawyerRef[];
  /** actor_account_id (商談イベントのみ)。 詳細表示の操作者名解決に使う。 */
  actorAccountId?: string | null;
}

export function entryToEvents(entry: DialogueLog): DisplayEvent[] {
  // timeline.md §4.2: 次のアクションはカードに出さない (営業アクションタブで一元管理)。
  const badges: string[] = [];
  return [
    {
      id: `entry:${entry.id}`,
      category: 'dialog',
      icon: DIALOGUE_LOG_TYPE_ICONS[entry.type] ?? '🗒️',
      typeLabel: DIALOGUE_LOG_TYPE_LABELS[entry.type] ?? entry.type,
      occurred_at: entry.occurred_at,
      title: entry.title,
      // 議事録本文 (body) をカードに出す。 title は種別ラベル (「議事録」 等)、
      // 中身は body なので、 これを summary に載せて表示する (改行は CSS で保持)。
      summary: entry.body ?? null,
      badges: badges.length > 0 ? badges : undefined,
      hasMinutes: !!entry.body,
      participantLawyerIds: entry.participant_lawyer_ids,
      relatedOpportunityId: entry.related_opportunity_id,
    },
  ];
}

/// カードの種別ラベルはサブフィルタ (CONTACT_LOG_TYPE_FILTER_OPTIONS) と同一表記
/// (86eyaj73w: 「お問い合わせ」→「問い合わせ」の統一を含む)。
const CONTACT_LOG_TYPE_META: Record<
  ContactLogType,
  { icon: string; label: string }
> = {
  inquiry: { icon: '✉️', label: '問い合わせ' },
  seminar: { icon: '🎓', label: 'セミナー' },
  document_download: { icon: '📥', label: '資料DL' },
  useful_material: { icon: '📄', label: 'お役立ち資料' },
  in_person_event: { icon: '📅', label: 'リアルイベント' },
  business_card_exchange: { icon: '🪪', label: '名刺交換' },
  community: { icon: '👥', label: 'コミュニティ' },
  referral: { icon: '🤝', label: '紹介' },
  other: { icon: '🧭', label: 'その他' },
};

/// サブフィルタの表示順・表記の単一ソース (86eyaj73w の固定順)。
export const CONTACT_LOG_TYPE_FILTER_OPTIONS: ReadonlyArray<{
  value: ContactLogType;
  label: string;
}> = [
  { value: 'inquiry', label: '問い合わせ' },
  { value: 'seminar', label: 'セミナー' },
  { value: 'document_download', label: '資料DL' },
  { value: 'useful_material', label: 'お役立ち資料' },
  { value: 'in_person_event', label: 'リアルイベント' },
  { value: 'business_card_exchange', label: '名刺交換' },
  { value: 'community', label: 'コミュニティ' },
  { value: 'referral', label: '紹介' },
  { value: 'other', label: 'その他' },
];

const CONTACT_LOG_SOURCE_LABELS: Record<ContactLogSource, string> = {
  automatic: '自動',
  manual: '手動',
  cv: 'CV',
  external: '外部',
};

export function contactLogToEvents(log: ContactLog): DisplayEvent[] {
  const meta =
    CONTACT_LOG_TYPE_META[log.contact_log_type] ?? CONTACT_LOG_TYPE_META.other;
  const sourceLabel = CONTACT_LOG_SOURCE_LABELS[log.source] ?? log.source;
  const title = log.subject?.trim() || meta.label;
  return [
    {
      id: `contact_log:${log.id}`,
      category: 'contact_log',
      contactLogType: log.contact_log_type,
      icon: meta.icon,
      typeLabel: meta.label,
      occurred_at: log.occurred_at,
      title,
      summary: log.subject?.trim() ? null : log.behavior_type,
      badges: [sourceLabel],
      lawyerId: log.lawyer_id,
    },
  ];
}

/** `YYYY-MM-DDTHH:mm:ssZ` → `YYYY-MM-DD HH:mm` (分まで)。日付のみの値はそのまま返す。 */
export function formatDateTime(iso: string): string {
  if (iso.length >= 16 && iso.includes('T'))
    return iso.slice(0, 16).replace('T', ' ');
  return iso.slice(0, 10);
}

function salesStatusLabel(raw: string): string {
  return SALES_STATUS_LABELS[raw as SalesStatus] ?? raw;
}

function lostReasonLabel(raw: string): string {
  return LOST_REASON_LABELS[raw as LostReason] ?? raw;
}

function contractStatusLabel(raw: string): string {
  return CONTRACT_STATUS_LABELS[raw as ContractStatus] ?? raw;
}

function contractPipelineLabel(raw: string): string {
  return CONTRACT_PIPELINE_LABELS[raw as ContractPipeline] ?? raw;
}

const RENEWAL_METHOD_LABELS: Record<string, string> = {
  manual: '手動更新',
  automatic: '自動更新',
  auto: '自動更新',
};

function renewalMethodLabel(raw: string): string {
  return RENEWAL_METHOD_LABELS[raw] ?? raw;
}

/** MRR 未設定 (`null`) と 0 円 (`"0"`) を区別して表示する (v1.0 Failure and Fallback)。 */
function formatMrr(value: string | null): string {
  if (value === null) return '未設定';
  const num = Number(value);
  if (Number.isNaN(num)) return '未設定';
  return `${num.toLocaleString('ja-JP')}円`;
}

const DEAL_EVENT_TITLES: Record<DealEventKind, string> = {
  deal_opened: '商談開始',
  deal_status_changed: 'ステータス変更',
  deal_migrated: '移行時点の商談状態',
};

/** 商談イベント (E-1/E-2/E-M) を 1 件の DisplayEvent に変換する。 */
export function dealEventToEvents(
  kind: DealEventKind,
  occurred_at: string,
  event: DealEvent,
): DisplayEvent[] {
  const badges: string[] = [];
  if (kind === 'deal_migrated') {
    badges.push('データ移行（過去の商談開始日を示すものではありません）');
  }

  const primaryFields: DisplayField[] = [
    { label: 'ソリューション', value: event.product_name ?? 'ソリューション未設定' },
  ];
  if (kind === 'deal_opened') {
    primaryFields.push({
      label: '開始時ステータス',
      value: salesStatusLabel(event.to_sales_status),
    });
  } else if (kind === 'deal_migrated') {
    primaryFields.push({
      label: '切替時点ステータス',
      value: salesStatusLabel(event.to_sales_status),
    });
  } else {
    primaryFields.push({
      label: 'ステータス',
      value: `${salesStatusLabel(event.from_sales_status)} → ${salesStatusLabel(event.to_sales_status)}`,
    });
  }

  const conditionalFields: DisplayField[] = [];
  if (event.lost_reason) {
    conditionalFields.push({
      label: '失注理由',
      value: `${lostReasonLabel(event.lost_reason)}${event.lost_reason_note ? `（${event.lost_reason_note}）` : ''}`,
    });
  }

  const detailFields: DisplayField[] = [
    { label: '記録日時', value: formatDateTime(event.recorded_at) },
  ];

  return [
    {
      id: `${kind}:${event.event_id}`,
      category: 'sales_event',
      icon: SALES_EVENT_ICON,
      typeLabel: DEAL_EVENT_TITLES[kind],
      occurred_at,
      // v1.0 E-1「action_title がある場合は商談を識別する補助情報」
      // = 元の営業アクション (source_record.display_name) をタイトルへ載せる。
      // 共通ヘッダーが要求する「生成元」の表示もこれで兼ねる (重複表示を避ける)。
      title: `${DEAL_EVENT_TITLES[kind]}${event.source_record.display_name ? `: ${event.source_record.display_name}` : ''}`,
      summary: null,
      badges: badges.length > 0 ? badges : undefined,
      primaryFields,
      conditionalFields: conditionalFields.length > 0 ? conditionalFields : undefined,
      detailFields,
      targetLawyers: event.target_lawyers,
      actorAccountId: event.actor_account_id,
    },
  ];
}

const CONTRACT_EVENT_TITLES: Record<ContractEventKind, string> = {
  contract_concluded: '契約締結',
  contract_renewed: '契約更新',
};

/**
 * 契約イベント (E-3/E-4) を 1 件の DisplayEvent に変換する。
 *
 * `occurred_at` は渡さず契約自身の `occurred_on` (DATE 粒度) を表示日時として
 * 使う — トップレベルの正規化済み timestamp (JST 00:00 の架空時刻を含む) を
 * 表示用に使わないことで、v1.0「日付のみの契約イベントへ表示用の
 * 架空時刻を付けない」を守る (Neo/Copilot レビュー)。
 */
export function contractEventToEvents(
  kind: ContractEventKind,
  event: ContractEvent,
): DisplayEvent[] {
  const badges: string[] = [];
  if (event.is_individual_contract) badges.push('個人契約');
  if (event.auto_renewal) badges.push('自動更新設定あり');

  const solutionNames = event.solutions.map(
    (s) => s.display_name ?? 'ソリューション不明',
  );

  const primaryFields: DisplayField[] = [
    { label: '契約名', value: event.deal_name ?? '未設定' },
    { label: '契約種別', value: event.contract_type ?? '未設定' },
    {
      label: 'ソリューション',
      value: solutionNames.length > 0 ? solutionNames.join('、') : 'ソリューション未設定',
    },
  ];

  if (kind === 'contract_concluded') {
    primaryFields.push(
      { label: '契約締結日', value: event.occurred_on },
      { label: '契約開始日', value: event.period_starts_on ?? '未設定' },
      { label: '契約終了日', value: event.period_ends_on ?? '未設定' },
      { label: 'MRR（月額）', value: formatMrr(event.monthly_amount) },
    );
  } else {
    const isAutomatic = event.renewal_method === 'automatic' || event.renewal_method === 'auto';
    primaryFields.push(
      { label: isAutomatic ? '更新確定日' : '更新締結日', value: event.occurred_on },
      { label: '更新開始日', value: event.period_starts_on ?? '未設定' },
      { label: '更新終了日', value: event.period_ends_on ?? '未設定' },
      { label: '更新時MRR', value: formatMrr(event.monthly_amount) },
    );
  }

  const conditionalFields: DisplayField[] = [];
  if (
    kind === 'contract_renewed' &&
    event.previous_monthly_amount !== event.monthly_amount
  ) {
    conditionalFields.push({
      label: 'MRR変更',
      value: `${formatMrr(event.previous_monthly_amount)} → ${formatMrr(event.monthly_amount)}`,
    });
  }
  if (kind === 'contract_renewed' && event.renewal_method) {
    conditionalFields.push({
      label: '更新方式',
      value: renewalMethodLabel(event.renewal_method),
    });
  }
  if (event.pipeline) {
    conditionalFields.push({
      label: '契約区分',
      value: contractPipelineLabel(event.pipeline),
    });
  }
  if (event.event_time_status) {
    conditionalFields.push({
      label: '契約ステータス（当時）',
      value: contractStatusLabel(event.event_time_status),
    });
  }
  // Q-4 (採用): 当時値と現在値を別ラベルで表示する。現在値は同一契約の全有効
  // イベントの末尾 1 件だけに付与される (backend `project_contract_events`)。
  if (event.current_status) {
    conditionalFields.push({
      label: '契約ステータス（現在）',
      value: contractStatusLabel(event.current_status),
    });
  }

  const detailFields: DisplayField[] = [
    { label: '記録日時', value: formatDateTime(event.recorded_at) },
  ];

  return [
    {
      // 安定イベント ID (`event.event_id`) をキーに含める。契約 ID + 発生日だけ
      // だと同日複数更新/訂正で衝突し、 keyed each (Timeline.svelte) が壊れる
      // (Neo/Copilot レビュー)。
      id: `${kind}:${event.event_id}`,
      category: 'sales_event',
      icon: SALES_EVENT_ICON,
      typeLabel: CONTRACT_EVENT_TITLES[kind],
      occurred_at: event.occurred_on,
      title: `${CONTRACT_EVENT_TITLES[kind]}${event.deal_name ? `: ${event.deal_name}` : ''}`,
      summary: null,
      badges: badges.length > 0 ? badges : undefined,
      primaryFields,
      conditionalFields: conditionalFields.length > 0 ? conditionalFields : undefined,
      detailFields,
      targetLawyers: event.target_lawyers,
      actorAccountId: event.actor_account_id,
    },
  ];
}

/**
 * `TimelineItem` の集合を `DisplayEvent` 配列に展開する。
 *
 * backend の返却順 (全順序: 業務日付降順 → 同日は実時刻降順 → 日付のみは
 * `recorded_at` 降順 → 最後に安定イベント ID 降順 — v1.0 Event Time and
 * Ordering) を **そのまま** 使う。ここでの再ソートは行わない — 1 item = 1
 * card の現在は、カテゴリ tie-break での再ソートが同時刻の混在ページで
 * backend の最終 tie-break (安定イベント ID 降順) を上書きしてしまうため
 * (Neo/Copilot レビュー)。複数ソース (entry/contact_log 等) をマージする側が
 * 出てきた場合も、backend と同じ全順序キーでマージすること — 独自の
 * tie-break を発明しない。
 */
export function expandTimelineItems(
  items: ReadonlyArray<TimelineItem>,
): DisplayEvent[] {
  const out: DisplayEvent[] = [];
  for (const item of items) {
    switch (item.kind) {
      case 'entry':
        out.push(...entryToEvents(item.entry));
        break;
      case 'contact_log':
        out.push(...contactLogToEvents(item.contact_log));
        break;
      case 'deal_opened':
      case 'deal_status_changed':
      case 'deal_migrated':
        out.push(...dealEventToEvents(item.kind, item.occurred_at, item.deal_event));
        break;
      case 'contract_concluded':
      case 'contract_renewed':
        out.push(...contractEventToEvents(item.kind, item.contract_event));
        break;
    }
  }
  return out;
}
